import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { ScrollText, Search, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      const response = await api.get('/audit-logs');
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'bg-green-500/20 text-green-400 border-green-500/30',
      update: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      delete: 'bg-red-500/20 text-red-400 border-red-500/30',
      start_scan: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      generate_ai_insights: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      compliance_check: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    };
    return colors[action] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
  };

  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAction = actionFilter === 'all' || log.action === actionFilter;
    return matchesSearch && matchesAction;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading audit logs...</div>
      </div>
    );
  }

  return (
    <div data-testid="audit-logs-container" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-rajdhani font-bold text-4xl text-white text-shadow-neon">Audit Logs</h1>
          <p className="text-zinc-400 font-inter mt-1">Complete activity tracking and monitoring</p>
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="search-logs-input"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-950/50 border-white/10 text-white"
            />
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger data-testid="filter-action-select" className="w-full md:w-48 bg-zinc-950/50 border-white/10 text-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10 text-white">
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="create">Create</SelectItem>
              <SelectItem value="update">Update</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
              <SelectItem value="start_scan">Start Scan</SelectItem>
              <SelectItem value="generate_ai_insights">AI Insights</SelectItem>
              <SelectItem value="compliance_check">Compliance Check</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <ScrollText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 font-inter">No audit logs found</p>
            </div>
          ) : (
            filteredLogs.map((log) => (
              <div
                key={log.id}
                data-testid={`audit-log-${log.id}`}
                className="glass-hover p-4 rounded-lg border border-white/10 hover:border-indigo-500/30 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-mono uppercase border ${getActionColor(
                        log.action
                      )}`}
                    >
                      {log.action}
                    </span>
                    
                    <div className="flex-1">
                      <p className="text-white font-inter text-sm">
                        <span className="font-semibold">{log.action}</span> on{' '}
                        <span className="text-indigo-400">{log.resource_type}</span>
                        {log.resource_id && (
                          <span className="text-zinc-500 text-xs ml-2">ID: {log.resource_id.substring(0, 8)}...</span>
                        )}
                      </p>
                      <p className="text-zinc-500 text-xs mt-1 font-mono">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-zinc-400 text-xs font-inter">User ID</p>
                    <p className="text-zinc-300 text-xs font-mono">{log.user_id.substring(0, 8)}...</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}